import { HelmetDexteel } from "@dexteel/mesf-core";
import {
	Alert,
	Button,
	Grid2,
	Paper,
	Snackbar,
	Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
	ColDef,
	GetContextMenuItemsParams,
	MenuItemDef,
	ValueFormatterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { get } from "lodash-es";
import { useCallback, useMemo, useState } from "react";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
import { UpsertDeleteRecipient } from "./components/UpsertDeleteRecipient";
import type { EmailRecipient, ModalMode } from "./models/EmailRecipient";
import { getEmailRecipients } from "./repositories/EmailRecipientsRepository";

const gridStyle = {
	height: "calc(100vh - 280px)",
	minHeight: 320,
	width: "100%",
};

export const EmailRecipientsPage = () => {
	const queryClient = useQueryClient();

	const [selected, setSelected] = useState<EmailRecipient | null>(null);
	const [modal, setModal] = useState<ModalMode>("");
	const [notification, setNotification] = useState<string>("");

	const { data, isLoading } = useQuery({
		queryKey: ["emailRecipients"],
		queryFn: getEmailRecipients,
	});

	const rows = useMemo(
		() => get(data, "data.tables[0].rows", []) as EmailRecipient[],
		[data],
	);

	const columns = useMemo<ColDef[]>(
		() => [
			{ field: "DisplayName", headerName: "Name", flex: 2 },
			{ field: "EmailAddress", headerName: "Email Address", flex: 3 },
			{ field: "TemplateCount", headerName: "Templates", width: 120 },
			{
				field: "IsActive",
				headerName: "Active",
				width: 100,
				valueFormatter: (p: ValueFormatterParams) => (p.value ? "Yes" : "No"),
			},
		],
		[],
	);

	const openNew = useCallback(() => {
		setSelected(null);
		setModal("new");
	}, []);

	const getContextMenuItems = useCallback(
		(e: GetContextMenuItemsParams) => {
			const row = e?.node?.data as EmailRecipient | undefined;
			const result: MenuItemDef[] = [
				{
					name: "New Recipient",
					action: openNew,
					icon: '<i class="fas fa-plus"></i>',
				},
			];
			if (row) {
				result.push(
					{
						name: "Edit Recipient",
						action: () => {
							setSelected(row);
							setModal("edit");
						},
						icon: '<i class="fas fa-edit"></i>',
					},
					{
						name: "Delete Recipient",
						action: () => {
							setSelected(row);
							setModal("delete");
						},
						icon: '<i class="fas fa-trash"></i>',
					},
				);
			}
			return result;
		},
		[openNew],
	);

	const closeModal = (savedMessage?: string) => {
		setModal("");
		if (savedMessage) {
			setNotification(savedMessage);
			queryClient.invalidateQueries({ queryKey: ["emailRecipients"] });
			queryClient.invalidateQueries({ queryKey: ["templateRecipients"] });
		}
	};

	return (
		<>
			<HelmetDexteel title="Email Recipients - Configuration" />
			<Grid2
				size={{ md: 12, xs: 12 }}
				sx={{ width: "100%", height: "100%", padding: 1 }}
			>
				<Typography variant="h5" style={{ fontWeight: 600 }}>
					Email Recipients
				</Typography>

				<Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
					People a template can notify when it generates work orders. An address
					is typed here once and picked from the template.
				</Typography>

				<Paper elevation={1} sx={{ p: 1, borderRadius: "0.75rem" }}>
					<div style={gridStyle}>
						<AgGridReact
							rowData={rows}
							columnDefs={columns}
							defaultColDef={globalDefaultColDef}
							rowSelection="single"
							loading={isLoading}
							getContextMenuItems={getContextMenuItems}
							onRowDoubleClicked={(e) => {
								setSelected(e.data ?? null);
								setModal("edit");
							}}
							pagination={false}
							overlayNoRowsTemplate="No email recipients yet"
						/>
					</div>
				</Paper>

				<Grid2 container justifyContent="flex-end" sx={{ mt: 1 }}>
					<Grid2 size={{ md: 2, xs: 12 }}>
						<Button
							variant="contained"
							color="primary"
							onClick={openNew}
							fullWidth
						>
							NEW RECIPIENT
						</Button>
					</Grid2>
				</Grid2>
			</Grid2>

			<UpsertDeleteRecipient
				modal={modal}
				recipient={modal === "new" ? null : selected}
				onHide={closeModal}
			/>

			<Snackbar
				open={!!notification}
				autoHideDuration={2500}
				onClose={() => setNotification("")}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="success" onClose={() => setNotification("")}>
					{notification}
				</Alert>
			</Snackbar>
		</>
	);
};
